import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { useIdProvider, useProvidedId } from '../hooks/id-provider.js';
import { useAutofocus } from '../hooks/use-autofocus.js';
import { useFocusAndReturn } from './dialog-primitive.hooks.js';

/**
 * Root component for an unstyled (“headless”) "dialog" (interaction point to get information /
 * approval from a user) Use `DialogPrimitive` instead of `Dialog` when you need a completely
 * custom-styled dialog component.
 *
 * @note While common for focus to loop around inside dialogs, it isn't a hard requirement,
 * and so the DialogPrimitive doesn't include a `TabLoop` by default. If you need focus to loop
 * around, you can wrap the dialog in a `TabLoop` component.
 */
const Root = forwardRef(({ tabIndex = -1, open = true, __internalCaptureDisplayNone, __internalOnBeforeFocusReturn, ...propsIn }, ref) => {
    const [id, IdProvider] = useIdProvider();
    const props = useFocusAndReturn({ ...propsIn, ref }, {
        open,
        ignoreDisplayNone: __internalCaptureDisplayNone,
        onBeforeFocusReturn: __internalOnBeforeFocusReturn,
    });
    return (jsx(IdProvider, { value: id, children: jsx("div", { "data-fpl-component": "primitive", ...props, role: "dialog", "aria-labelledby": id, tabIndex: tabIndex }) }));
});
Root.displayName = 'DialogPrimitive.Root';
/**
 * A label element to be used inside a `DialogPrimitive`. This label will automatically be used as
 * the `aria-labelledby` attribute for the dialog root.
 */
const Label = forwardRef(({ children, autofocus = false, ...props }, refIn) => {
    const id = useProvidedId();
    const ref = useAutofocus(refIn, autofocus);
    return (jsx("h2", { ...props, ref: ref, id: id, 
        // From {@link https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/}
        // If the dialog content includes semantic structures, such as lists, tables, or multiple
        // paragraphs, that need to be perceived in order to easily understand the content, i.e., if
        // the content would be difficult to understand when announced as a single unbroken string,
        // then it is advisable to add tabindex="-1" to a static element at the start of the content
        // and initially focus that element. This makes it easier for assistive technology users to
        // read the content by navigating the semantic structures.
        tabIndex: autofocus ? -1 : undefined, children: children }));
});
Label.displayName = 'DialogPrimitive.Label';

export { Label, Root };
//# sourceMappingURL=dialog-primitive.js.map
